import { APITypesV1 } from "@cur8/api-client";
import {
  BottomSheet,
  Button,
  Icon16ArrowLeft,
  Icon24Alert,
  Icon16Checkmark,
  Icon16Close,
  IconButton,
  Typography,
} from "@cur8/maneki";
import { Measurement } from "@cur8/rich-entity";
import { useStoreMeasurementMutation } from "hooks/mutations/useStoreMeasurementMutation";
import { useRiskRangesQuery } from "hooks/queries/useRiskRangesQuery";
import { useVisitMeasurementsQuery } from "hooks/queries/useVisitMeasurementsQuery";
import { useCallback, useMemo, useState } from "react";
import { Link } from "render/ui/trigger/Link";
import {
  getMeasurementConfigs,
  getMeasurementGrid,
  isGroup,
  MetricConfig,
  MetricGroup,
} from "./measurementConfig";
import { GroupTile } from "./GroupTile";
import { MeasurementEntry } from "./MeasurementEntry";
import { MeasurementTile } from "./MeasurementTile";
import styles from "./styles.module.sass";

interface MeasurementsBottomSheetProps {
  patientId: string;
  visitId: string;
  countryCode: string | undefined;
  generation: APITypesV1.RoomGeneration | undefined;
  isOpen: boolean;
  onClose: () => void;
}

export function MeasurementsBottomSheet({
  patientId,
  visitId,
  countryCode,
  generation,
  isOpen,
  onClose,
}: MeasurementsBottomSheetProps) {
  const [activeGroup, setActiveGroup] = useState<MetricGroup | null>(null);
  const [activeMetric, setActiveMetric] = useState<MetricConfig | null>(null);

  const configs = useMemo(
    () => getMeasurementConfigs(generation),
    [generation]
  );

  const grid = useMemo(() => getMeasurementGrid(generation), [generation]);

  const metricNames = useMemo(
    () => configs.map((c) => c.metricName),
    [configs]
  );

  const { data: measurementSummary } = useVisitMeasurementsQuery({
    patientId,
    visitId,
    options: { refetchInterval: 30_000 },
  });

  const { data: riskRanges } = useRiskRangesQuery({ metricNames });

  const storeMeasurement = useStoreMeasurementMutation({ patientId, visitId });

  const existingByMetric = useMemo(() => {
    const map = new Map<string, Measurement>();
    for (const m of measurementSummary?.measurements ?? []) {
      if (m.metricName) {
        map.set(m.metricName, m);
      }
    }
    return map;
  }, [measurementSummary]);

  const completedCount = useMemo(
    () => configs.filter((c) => existingByMetric.has(c.metricName)).length,
    [configs, existingByMetric]
  );

  const hubTitle = `Measurements (${completedCount}/${configs.length})`;

  const roomDescription = useMemo(() => {
    if (generation === APITypesV1.RoomGeneration.Gen1) {
      return "You are in a Gen1 room – please enter measurements manually.";
    }
    if (generation === APITypesV1.RoomGeneration.Gen2) {
      return "You are in a Gen2 room – once the devices send measurements you will see them appearing. You can still manually override measurements if required.";
    }
    return undefined;
  }, [generation]);

  const isAutoImportedMetric = (metric: MetricConfig) =>
    !!metric.autoImported && generation === APITypesV1.RoomGeneration.Gen2;

  // Back from entry form: return to group if one is active, otherwise hub
  const handleBackFromEntry = useCallback(() => {
    storeMeasurement.reset();
    setActiveMetric(null);
  }, [storeMeasurement]);

  // Back from group grid: return to hub
  const handleBackFromGroup = useCallback(() => {
    setActiveGroup(null);
  }, []);

  const handleClose = useCallback(() => {
    setActiveMetric(null);
    setActiveGroup(null);
    onClose();
  }, [onClose]);

  const tileGrid = activeGroup ? activeGroup.metrics : null;

  return (
    <BottomSheet isOpen={isOpen} onClose={handleClose} showCloseIcon={false}>
      <div className={styles.content}>
        {activeMetric ? (
          <>
            <div className={styles.breadcrumbRow}>
              <div className={styles.breadcrumb}>
                <Link onPress={handleBackFromEntry}>
                  <span className={styles.breadcrumbLink}>
                    <span className={styles.breadcrumbIcon}>
                      <Icon16ArrowLeft />
                    </span>
                    <Typography variant="label-s">
                      {activeGroup ? activeGroup.label : "Measurements"}
                    </Typography>
                  </span>
                </Link>
                <span className={styles.breadcrumbCurrent}>
                  <Typography variant="label-s">
                    / {activeMetric.label}
                  </Typography>
                </span>
              </div>
              <IconButton
                aria-label="Close"
                variant="tertiary"
                onPress={handleClose}
              >
                <Icon16Close />
              </IconButton>
            </div>
            <div className={styles.entryTitle}>
              <Typography variant="display-s">{activeMetric.label}</Typography>
            </div>
            <MeasurementEntry
              config={activeMetric}
              visitId={visitId}
              isAutoImported={isAutoImportedMetric(activeMetric)}
              existing={existingByMetric.get(activeMetric.metricName)}
              riskRanges={
                riskRanges?.find(
                  (r) => r.metricName === activeMetric.metricName
                )?.riskRanges
              }
              countryCode={countryCode}
              storeMeasurement={storeMeasurement}
              onBack={handleBackFromEntry}
              onDone={handleBackFromEntry}
            />
          </>
        ) : tileGrid ? (
          <>
            <div className={styles.breadcrumbRow}>
              <div className={styles.breadcrumb}>
                <Link onPress={handleBackFromGroup}>
                  <span className={styles.breadcrumbLink}>
                    <span className={styles.breadcrumbIcon}>
                      <Icon16ArrowLeft />
                    </span>
                    <Typography variant="label-s">Measurements</Typography>
                  </span>
                </Link>
                <span className={styles.breadcrumbCurrent}>
                  <Typography variant="label-s">
                    / {activeGroup!.label}
                  </Typography>
                </span>
              </div>
              <IconButton
                aria-label="Close"
                variant="tertiary"
                onPress={handleClose}
              >
                <Icon16Close />
              </IconButton>
            </div>
            <div className={styles.entryTitle}>
              <Typography variant="display-s">{activeGroup!.label}</Typography>
            </div>
            <div className={styles.tileGrid}>
              {tileGrid.map((config) => (
                <MeasurementTile
                  key={config.metricName}
                  config={config}
                  measurement={existingByMetric.get(config.metricName)}
                  onClick={() => setActiveMetric(config)}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className={styles.breadcrumbRow}>
              <div className={styles.entryTitle}>
                <Typography variant="display-s">{hubTitle}</Typography>
              </div>
              <IconButton
                aria-label="Close"
                variant="tertiary"
                onPress={handleClose}
              >
                <Icon16Close />
              </IconButton>
            </div>
            {roomDescription && (
              <Typography variant="body-m" color="subtle">
                {roomDescription}
              </Typography>
            )}
            <div className={styles.tileGrid}>
              {grid.map((item) =>
                isGroup(item) ? (
                  <GroupTile
                    key={item.label}
                    group={item}
                    existingByMetric={existingByMetric}
                    onClick={() => setActiveGroup(item)}
                  />
                ) : (
                  <MeasurementTile
                    key={item.metricName}
                    config={item}
                    measurement={existingByMetric.get(item.metricName)}
                    onClick={() => setActiveMetric(item)}
                  />
                )
              )}
            </div>
          </>
        )}
      </div>

      {activeGroup != null && activeMetric == null ? (
        <Button variant="secondary" onPress={handleBackFromGroup} fillContainer>
          Back
        </Button>
      ) : undefined}

      {activeMetric == null &&
      activeGroup == null &&
      completedCount === configs.length ? (
        <Button variant="primary" onPress={handleClose} fillContainer>
          Done
        </Button>
      ) : undefined}
    </BottomSheet>
  );
}

export function MeasurementsChip({
  patientId,
  visitId,
  generation,
  countryCode,
}: {
  patientId: string;
  visitId: string;
  generation: APITypesV1.RoomGeneration | undefined;
  countryCode: string | undefined;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const configs = useMemo(
    () => getMeasurementConfigs(generation),
    [generation]
  );

  const { data: measurementSummary } = useVisitMeasurementsQuery({
    patientId,
    visitId,
    options: { refetchInterval: 30_000 },
  });

  const completedCount = useMemo(() => {
    const metricNames = new Set(configs.map((c) => c.metricName));
    return (
      measurementSummary?.measurements.filter(
        (m) => m.metricName && metricNames.has(m.metricName)
      ).length ?? 0
    );
  }, [configs, measurementSummary]);

  const chipState =
    completedCount === configs.length
      ? "done"
      : completedCount > 0
        ? "partial"
        : "none";

  return (
    <>
      <button
        type="button"
        className={styles.chip}
        data-state={chipState}
        onClick={() => setIsOpen(true)}
      >
        <Typography variant="body-xs">
          {chipState === "done"
            ? `Measurements done`
            : `Measurements - ${configs.length - completedCount} missing`}
        </Typography>
        {chipState === "done" ? <Icon16Checkmark /> : <Icon24Alert />}
      </button>

      <MeasurementsBottomSheet
        patientId={patientId}
        visitId={visitId}
        countryCode={countryCode}
        generation={generation}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
}
